import { Controller } from "@hotwired/stimulus";

// 管理画面のメッセージ作成画面（admin/customer_notification_bulk_sends/_form.html.erb）。
// 宛先を1人1つのチップで出し、多いときは人数だけのチップにまとめて、押すと宛先の一覧（1人ずつ外せる）を開く。
// 宛先は recipients（[{ id, name, email }]）に持ち、描き直しは recipientsValueChanged に集める。
// ERB の data-action や Stimulus から呼ばれるメソッドは普通の名前、このファイルの中だけで使うメソッドは # を付けた private にしている。
//
// 探して足す欄は customer-search（tom-select）が持ち、選ばれたらここで宛先に移して欄を空にする。
// まとめて追加では、「読み取って追加」を押したときに、貼った文字からメールアドレスを拾い、ユーザーかどうかをサーバに1回だけ問い合わせる。

// 貼った文字から、メールアドレスの形をした部分を拾う。
// Excel の表をコピーするとタブ区切りになるため、前後の区切りの文字は問わない。
const EMAIL_PATTERN = /[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+/g;

// 宛先に入れられなかった理由（サーバが返す reason）の、画面に出す言葉。
const SKIPPED_REASON_LABELS = { not_found: "見つからない", unregistered: "仮会員" };
// 宛先欄に1人1つのチップで出す人数。超えたら「宛先 N人 ▸」の1つにまとめる。
const CHIP_LIMIT = 10;
// 宛先の一覧に一度に描く行数。超えた分は絞り込みで探す。
const LIST_ROW_LIMIT = 300;

export class BulkSendComposeController extends Controller {
  static targets = [
    // 送信で届く hidden の項目
    "recipientTypeField", "customerIdsField", "confirmedCountField",
    // 宛先欄。全員のチップの欄と、選んだ人の欄（チップ・探す欄・ボタン）を、選び方で出し分ける
    "allRecipientsArea", "selectedRecipientsArea", "chipList", "chipTemplate", "summaryChip", "summaryCount", "recipientListButton", "recipientCount",
    // 追加できなかったアドレス
    "skippedDetails", "skippedCount", "skippedList", "skippedItemTemplate",
    // 件名・メール
    "titleInput", "emailCheckbox",
    // まとめて追加のダイアログ
    "pasteDialog", "pasteInput", "pasteStatus", "pasteReadButton",
    // 宛先の一覧のダイアログ
    "recipientListDialog", "recipientListCount", "recipientListFilter", "recipientListBody", "recipientListRowTemplate", "recipientListMore",
    // 送る前の確認のダイアログ
    "confirmDialog", "confirmCount", "confirmTitle", "confirmWithEmail", "confirmWithoutEmail", "confirmTypedCountArea", "confirmSendButton",
    // マイページでの見え方のダイアログ
    "previewDialog", "previewFrame",
  ];

  static outlets = ["customer-search"];

  static values = {
    lookupUrl: String,
    previewUrl: String,
    lookupLimit: Number,
    csrfToken: String,
    messageableCustomersCount: Number,
    emailLimit: Number,
    recipientType: String,
    recipients: Array,
  };

  disconnect() {
    this.lookupAbortController?.abort();
  }

  recipientTypeValueChanged() {
    const isAllRecipients = this.#isAllRecipients();
    this.recipientTypeFieldTarget.value = this.recipientTypeValue;
    this.allRecipientsAreaTarget.hidden = !isAllRecipients;
    this.selectedRecipientsAreaTarget.hidden = isAllRecipients;
    this.#refreshCount();
  }

  // 宛先のチップ・一覧・送る値を描き直す。
  recipientsValueChanged() {
    const recipients = this.recipientsValue;
    this.customerIdsFieldTarget.value = recipients.map((recipient) => recipient.id).join(",");

    this.#renderChips(recipients);
    this.recipientListButtonTarget.hidden = recipients.length === 0;

    if (this.recipientListDialogTarget.open) this.renderList();
    this.#excludeRecipientsFromSearch();
    this.#refreshCount();
  }

  // ページを開いた直後は、探す欄（customer-search）がまだつながっていないことがあるため、つながったときにも渡す。
  customerSearchOutletConnected() {
    this.#excludeRecipientsFromSearch();
  }

  // 探して足す欄で選ばれた人を宛先に移し、欄を空にする。
  addSearched(event) {
    const tomSelect = this.customerSearchOutlet.select;
    const option = tomSelect.options[event.target.value];
    // 欄を空にしたときにも change が来るため、選ばれていないときは何もしない。
    if (!option) return;

    this.#addRecipients([{ id: Number(option.id), name: option.name, email: option.email }]);
    tomSelect.clear(true);
  }

  removeRecipient({ params: { id } }) {
    this.recipientsValue = this.recipientsValue.filter((recipient) => recipient.id !== id);
  }

  selectAll() {
    this.recipientTypeValue = "all_registered";
  }

  clearAll() {
    this.recipientTypeValue = "selected";
  }

  // ダイアログは閉じても、中に貼った文字や知らせが残る。前回の続きにならないよう、開くたびに空に戻す。
  openPaste() {
    this.pasteInputTarget.value = "";
    this.pasteStatusTarget.textContent = "";
    this.pasteReadButtonTarget.disabled = false;
    this.pasteDialogTarget.showModal();
  }

  // 貼った文字からメールアドレスを拾い、ユーザーかどうかをサーバに問い合わせて、見つかった人を宛先に足す。
  readPasted() {
    const { emails, duplicates } = this.#extractEmails(this.pasteInputTarget.value);
    const error = this.#pasteError(emails);
    if (error) {
      this.pasteStatusTarget.textContent = error;
      return;
    }

    this.#lookupEmails(emails, duplicates);
  }

  openList() {
    this.recipientListFilterTarget.value = "";
    this.renderList();
    this.recipientListDialogTarget.showModal();
  }

  // 宛先の一覧を、絞り込みの文字に合う人だけ描く。
  renderList() {
    const keyword = this.recipientListFilterTarget.value.trim().toLowerCase();
    const matched = this.recipientsValue.filter((recipient) => this.#recipientMatches(recipient, keyword));
    const shown = matched.slice(0, LIST_ROW_LIMIT);

    this.recipientListCountTarget.textContent = this.recipientsValue.length.toLocaleString();
    this.recipientListBodyTarget.replaceChildren(...shown.map((recipient) => this.#buildListRow(recipient)));
    this.recipientListMoreTarget.hidden = matched.length <= shown.length;
    this.recipientListMoreTarget.textContent = `ほか ${(matched.length - shown.length).toLocaleString()}人は、絞り込んで探してください。`;
  }

  openConfirm() {
    // 件名・本文が空のときは、ブラウザの入力チェックを出し、確認のダイアログを開かない。
    if (!this.element.reportValidity()) return;

    const total = this.#recipientsCount();
    const withEmail = this.emailCheckboxTarget.checked;
    this.confirmedCountFieldTarget.value = total;
    this.confirmCountTarget.textContent = total.toLocaleString();
    this.confirmTitleTarget.textContent = this.titleInputTarget.value;
    this.confirmWithEmailTarget.hidden = !withEmail;
    this.confirmWithoutEmailTarget.hidden = withEmail;
    this.confirmTypedCountAreaTarget.hidden = !this.#isAllRecipients();
    this.confirmDialogTarget.showModal();
  }

  // 入力中のフォームをプレビューの URL に送り、返ってきた HTML を iframe に出す。
  // フォームの authenticity_token は送信（create）にしか使えないため、ページ全体で使えるトークンをヘッダーで送る。
  async openPreview() {
    this.previewFrameTarget.srcdoc = "";
    this.previewDialogTarget.showModal();
    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { Accept: "text/html", "X-CSRF-Token": this.csrfTokenValue },
        body: new FormData(this.element),
      });
      if (!response.ok) throw new Error(response.statusText);

      this.previewFrameTarget.srcdoc = await response.text();
    } catch {
      this.previewFrameTarget.srcdoc = "<p>プレビューを出せませんでした。もう一度お試しください。</p>";
    }
  }

  // 確認ダイアログを通らない送信（件名の欄で Enter を押したときなど）は止め、送信ボタンの二度押しで2回送らないようにする。
  lockSend(event) {
    const isSentFromConfirm = this.confirmDialogTarget.open && event.submitter === this.confirmSendButtonTarget;
    if (!isSentFromConfirm) {
      event.preventDefault();
      return;
    }

    this.confirmSendButtonTarget.disabled = true;
  }

  // 閉じるボタンか、ダイアログの外側を押したら閉じる。
  closeDialog(event) {
    const dialog = event.currentTarget;
    const isCloseButton = event.target.closest("[data-close-dialog]") !== null;
    const isOutside = event.target === dialog;
    if (isCloseButton || isOutside) dialog.close();
  }

  #isAllRecipients() {
    return this.recipientTypeValue === "all_registered";
  }

  #recipientsCount() {
    return this.#isAllRecipients() ? this.messageableCustomersCountValue : this.recipientsValue.length;
  }

  // 宛先の人数と、メールを選べるかを描き直す。
  #refreshCount() {
    const total = this.#recipientsCount();
    this.recipientCountTarget.textContent = total.toLocaleString();

    const isOverEmailLimit = total > this.emailLimitValue;
    if (isOverEmailLimit) this.emailCheckboxTarget.checked = false;
    this.emailCheckboxTarget.disabled = isOverEmailLimit;
  }

  // 宛先に入れた人を、探す欄の候補から外す。
  #excludeRecipientsFromSearch() {
    if (!this.hasCustomerSearchOutlet) return;

    this.customerSearchOutlet.excludedIdsValue = this.recipientsValue.map((recipient) => recipient.id);
  }

  // まだ宛先にいない人だけを、後ろに足す。
  #addRecipients(recipients) {
    const knownIds = new Set(this.recipientsValue.map((recipient) => recipient.id));
    const added = recipients.filter((recipient) => !knownIds.has(recipient.id));
    this.recipientsValue = [...this.recipientsValue, ...added];
  }

  // 宛先が CHIP_LIMIT 人までは1人1つのチップを並べ、超えたら人数のチップ1つにまとめる。
  #renderChips(recipients) {
    const isCollapsed = recipients.length > CHIP_LIMIT;
    this.summaryChipTarget.hidden = !isCollapsed;
    this.summaryCountTarget.textContent = recipients.length.toLocaleString();

    const chips = isCollapsed ? [] : recipients.map((recipient) => this.#buildChip(recipient));
    this.chipListTarget.replaceChildren(...chips);
  }

  #buildChip(recipient) {
    const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
    // 同じ氏名の人を見分けられるよう、ID も出す。
    chip.firstElementChild.textContent = `#${recipient.id} ${recipient.name}`;
    chip.title = recipient.email;
    chip.lastElementChild.dataset.bulkSendComposeIdParam = recipient.id;
    return chip;
  }

  // 貼った文字からアドレスを拾い、重複を除いたもの（emails）と、2回以上出てきたもの（duplicates）に分ける。
  #extractEmails(text) {
    const extracted = (text.match(EMAIL_PATTERN) || []).map((email) => email.toLowerCase());
    const emails = [...new Set(extracted)];
    const duplicates = [...new Set(extracted.filter((email, index) => extracted.indexOf(email) !== index))];
    return { emails, duplicates };
  }

  // サーバに問い合わせる前に分かる誤りの知らせ。誤りが無ければ null。
  #pasteError(emails) {
    if (emails.length === 0) return "メールアドレスが見つかりませんでした。";
    // サーバも同じ上限で断るが、2万件を送る前に知らせる。
    if (emails.length > this.lookupLimitValue) {
      return `一度に読み取れるのは ${this.lookupLimitValue.toLocaleString()}件までです（${emails.length.toLocaleString()}件ありました）。分けて貼り付けてください`;
    }
    return null;
  }

  // アドレスがユーザーかどうかをサーバに問い合わせ、見つかった人を宛先に足し、足せなかったアドレスを理由つきで出す。
  async #lookupEmails(emails, duplicates) {
    this.pasteReadButtonTarget.disabled = true;
    this.pasteStatusTarget.textContent = `${emails.length.toLocaleString()}件のアドレスを確認しています…`;
    // 前の問い合わせが残っていたら取り消す（古い結果が、新しい結果を上書きしないように）。
    this.lookupAbortController?.abort();
    this.lookupAbortController = new AbortController();
    try {
      const response = await fetch(this.lookupUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfTokenValue },
        body: JSON.stringify({ emails }),
        signal: this.lookupAbortController.signal,
      });
      const result = await response.json();
      if (!response.ok) {
        this.#showPasteError(result.error);
        return;
      }

      this.#addRecipients(result.customers);
      this.#showSkipped([
        ...result.skipped.map(({ email, reason }) => `${email}（${SKIPPED_REASON_LABELS[reason]}）`),
        ...duplicates.map((email) => `${email}（重複）`),
      ]);
      this.pasteDialogTarget.close();
    } catch (error) {
      // 取り消したときは、新しい問い合わせが結果を出すので、何もしない。
      if (error.name === "AbortError") return;

      this.#showPasteError("確認できませんでした。もう一度押してください。");
    }
  }

  // ダイアログに誤りを出し、もう一度押せるようにする。
  #showPasteError(message) {
    this.pasteStatusTarget.textContent = message;
    this.pasteReadButtonTarget.disabled = false;
  }

  #showSkipped(skipped) {
    this.skippedDetailsTarget.hidden = skipped.length === 0;
    this.skippedDetailsTarget.open = false;
    this.skippedCountTarget.textContent = skipped.length.toLocaleString();
    this.skippedListTarget.replaceChildren(
      ...skipped.map((text) => {
        const item = this.skippedItemTemplateTarget.content.firstElementChild.cloneNode();
        item.textContent = text;
        return item;
      }),
    );
  }

  // ID・氏名・メールアドレスのどれかに、絞り込みの文字が入っているか。
  // 絞り込みの欄が空のときは、空の文字はどの値にも含まれるので、全員が当たる。
  #recipientMatches(recipient, keyword) {
    const values = [String(recipient.id), recipient.name, recipient.email];
    return values.some((value) => value.toLowerCase().includes(keyword));
  }

  // 一覧の1行を、template の <tr> を複製して作る。
  // 「外す」のボタンには、押したときに removeRecipient がその人の id を受け取れるよう、id を持たせる。
  #buildListRow(recipient) {
    const row = this.recipientListRowTemplateTarget.content.firstElementChild.cloneNode(true);
    const [idCell, nameCell, emailCell, actionCell] = row.children;
    idCell.textContent = recipient.id;
    nameCell.textContent = recipient.name;
    emailCell.textContent = recipient.email;
    actionCell.firstElementChild.dataset.bulkSendComposeIdParam = recipient.id;
    return row;
  }
}
